import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Button from '@Components/Button';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { types } from '@Pages/Heatmap/HeatmapTooltipHeaders/constants';

import './TooltipContentOptions.scss';

function TooltipContentOptions({
  isRow,
  type,
  isReady,
  hasStructure,
  hasTargets,
  isSortByActivityActive,
  isAscending,
  sortByActivity,
  isLimitDisabled,
  limitTo,
  onCopyStructureToClipboard,
  onCopyStructureToQuery,
  onCopyStructureToFilter,
  onShowResultsContextForSelection,
}) {
  const isSubstance = type === types.substance;
  const isTarget = type === types.target;

  return (
    <div className={cn('heatmap-tooltip-content-options', { 'heatmap-tooltip-content-options--has-structure': hasStructure })}>
      {isReady ? (
        <ul className="heatmap-tooltip-content-options__sections">
          {/* Sort by activity */}
          <li
            className={cn('heatmap-tooltip-content-options__section', {
              'heatmap-tooltip-content-options__section--active': isSortByActivityActive,
            })}>
            <div className="heatmap-tooltip-content-options__option">
              <Button
                buttonConfig={{
                  label: 'Sort by activity',
                  ariaLabel: 'Sort by activity',
                  type: 'link',
                  size: 'sm',
                  e2eName: 'sort-by-activity',
                }}
                iconConfig={
                  isSortByActivityActive
                    ? {
                        name: 'check',
                        size: 'tiny',
                        position: 'before',
                      }
                    : undefined
                }
                onClick={() => sortByActivity()}
                data-click-delegation
                data-tracking="true"
                data-tracking-type="productfeatureused"
                data-tracking-action="Sort by activity"
              />
              {isSortByActivityActive && (
                <div className="heatmap-tooltip-content-options__sort-direction">
                  <Button
                    buttonConfig={{
                      ariaLabel: 'Sort by activity desc',
                      type: 'link',
                      size: 'sm',
                      e2eName: 'sort-by-activity-desc',
                      iconOnly: true,
                      className: cn({ active: !isAscending }),
                    }}
                    iconConfig={{
                      name: 'down',
                      size: 'tiny',
                    }}
                    onClick={() => sortByActivity(false)}
                    data-click-delegation
                    data-tracking="true"
                    data-tracking-type="productfeatureused"
                    data-tracking-action="Sort by activity"
                  />
                  <Button
                    buttonConfig={{
                      ariaLabel: 'Sort by activity asc',
                      type: 'link',
                      size: 'sm',
                      e2eName: 'sort-by-activity-asc',
                      iconOnly: true,
                      className: cn({ active: isAscending }),
                    }}
                    iconConfig={{
                      name: 'up',
                      size: 'tiny',
                    }}
                    onClick={() => sortByActivity(true)}
                    data-click-delegation
                    data-tracking="true"
                    data-tracking-type="productfeatureused"
                    data-tracking-action="Sort by activity"
                  />
                </div>
              )}
            </div>
          </li>
          {/* Limit to & exclude */}
          <li className="heatmap-tooltip-content-options__section">
            <Button
              buttonConfig={{
                label: `Limit to selected ${isRow ? 'row(s)' : 'column(s)'}`,
                ariaLabel: `Limit to selected ${isRow ? 'row(s)' : 'column(s)'}`,
                type: 'link',
                size: 'sm',
                e2eName: 'heatmap-limit-to',
                className: 'heatmap-tooltip-content-options__option',
              }}
              iconConfig={{
                name: 'screenshot',
                size: 'tiny',
              }}
              disabled={isLimitDisabled}
              onClick={() => limitTo()}
              data-click-delegation
              data-tracking="true"
              data-tracking-type="productfeatureused"
              data-tracking-action-text-content
            />
            <Button
              buttonConfig={{
                label: `Exclude selected ${isRow ? 'row(s)' : 'column(s)'}`,
                ariaLabel: `Exclude selected ${isRow ? 'row(s)' : 'column(s)'}`,
                type: 'link',
                size: 'sm',
                e2eName: 'heatmap-exclude',
                className: 'heatmap-tooltip-content-options__option',
              }}
              iconConfig={{
                name: 'forbidden',
                size: 'tiny',
              }}
              disabled={isLimitDisabled}
              onClick={() => limitTo({ exclude: true })}
              data-click-delegation
              data-tracking="true"
              data-tracking-type="productfeatureused"
              data-tracking-action-text-content
            />
          </li>
          {/* Copy structure */}
          {hasStructure && (
            <li className="heatmap-tooltip-content-options__section">
              <div className="heatmap-tooltip-content-options__option els-btn els-btn-sm els-btn-link">
                <span>Copy structure to</span>
                <svg className="els-btn-icon">
                  <use xlinkHref="#icon-chevron-right" />
                </svg>
                <div className="heatmap-tooltip-content-options__sub-options">
                  <div className="heatmap-tooltip-content-options__section">
                    <Button
                      buttonConfig={{
                        label: 'Clipboard',
                        ariaLabel: 'Copy structure to Clipboard',
                        type: 'link',
                        size: 'sm',
                        e2eName: 'heatmap-copy-to-clipboard',
                        className: 'heatmap-tooltip-content-options__option',
                      }}
                      onClick={onCopyStructureToClipboard}
                      data-click-delegation
                      data-tracking="true"
                      data-tracking-type="productfeatureused"
                      data-tracking-action="Copy structure to Clipboard"
                    />
                    <Button
                      buttonConfig={{
                        label: 'Query',
                        ariaLabel: 'Copy structure to Query',
                        type: 'link',
                        size: 'sm',
                        e2eName: 'heatmap-copy-to-query',
                        className: 'heatmap-tooltip-content-options__option',
                      }}
                      onClick={onCopyStructureToQuery}
                      data-click-delegation
                      data-tracking="true"
                      data-tracking-type="productfeatureused"
                      data-tracking-action="Copy structure to Query"
                    />
                  </div>
                </div>
              </div>
              <Button
                buttonConfig={{
                  label: 'Use as filter',
                  ariaLabel: 'Use as filter',
                  type: 'link',
                  size: 'sm',
                  e2eName: 'heatmap-use-as-filter',
                  className: 'heatmap-tooltip-content-options__option',
                }}
                onClick={onCopyStructureToFilter}
                data-click-delegation
                data-tracking="true"
                data-tracking-type="productfeatureused"
                data-tracking-action="Use as filter"
              />
            </li>
          )}
          {/* Details for Targets */}
          {isTarget && (
            <li className="heatmap-tooltip-content-options__section">
              <Button
                buttonConfig={{
                  label: 'Target summary',
                  ariaLabel: 'Target summary',
                  type: 'link',
                  size: 'sm',
                  e2eName: 'heatmap-target-summary',
                  className: 'heatmap-tooltip-content-options__option',
                }}
                disabled={!hasTargets}
                onClick={onShowResultsContextForSelection}
                data-click-delegation
                data-tracking="true"
                data-tracking-type="productfeatureused"
                data-tracking-action="Target summary"
              />
            </li>
          )}
          {/* Details for Substances */}
          {isSubstance && (
            <li className="heatmap-tooltip-content-options__section">
              <Button
                buttonConfig={{
                  label: 'All substance details',
                  ariaLabel: 'All substance details',
                  type: 'link',
                  size: 'sm',
                  e2eName: 'heatmap-all-substance-details',
                  className: 'heatmap-tooltip-content-options__option',
                }}
                onClick={onShowResultsContextForSelection}
                data-click-delegation
                data-tracking="true"
                data-tracking-type="productfeatureused"
                data-tracking-action="All substance details"
              />
            </li>
          )}
        </ul>
      ) : (
        <SimpleSpinner className="options-preview" />
      )}
    </div>
  );
}

TooltipContentOptions.defaultProps = {
  sortByActivity: () => {},
  limitTo: () => {},
  onCopyStructureToClipboard: () => {},
  onCopyStructureToQuery: () => {},
  onCopyStructureToFilter: () => {},
  onShowResultsContextForSelection: () => {},
};

TooltipContentOptions.propTypes = {
  isRow: PropTypes.bool,
  type: PropTypes.string,
  isReady: PropTypes.bool,
  hasStructure: PropTypes.bool,
  hasTargets: PropTypes.bool,
  isSortByActivityActive: PropTypes.bool,
  isAscending: PropTypes.bool,
  sortByActivity: PropTypes.func,
  isLimitDisabled: PropTypes.bool,
  limitTo: PropTypes.func,
  onCopyStructureToClipboard: PropTypes.func,
  onCopyStructureToQuery: PropTypes.func,
  onCopyStructureToFilter: PropTypes.func,
  onShowResultsContextForSelection: PropTypes.func,
};

export default TooltipContentOptions;
